import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../ui/card';
import { MapPin, Navigation, Lock, Tag, Sparkles, Building2 } from 'lucide-react';
import { dealFor, initialsOf, colorOf } from './dealUtils';

function Logo({ m }) {
  const [err, setErr] = useState(false);
  const name = m.business_name || m.name;
  if (m.business_logo_url && !err) {
    return <img src={m.business_logo_url} alt={name} onError={() => setErr(true)} className="w-full h-full object-cover" data-testid="merchant-logo-img" />;
  }
  const c = colorOf(name);
  return (
    <span className="font-outfit font-semibold text-lg" style={{ color: c }} data-testid="merchant-logo-initials">{initialsOf(name)}</span>
  );
}

export function MerchantDealCard({ m, tier, industry, distance, loggedIn }) {
  const deal = dealFor(m, tier);
  const name = m.business_name || m.name;
  const c = colorOf(name);
  const eliteExclusive = m.has_offer && m.elite_discount_pct > m.pro_discount_pct;
  return (
    <Link to={`/marketplace/${m.slug || m.id}`} className="group block min-w-0" data-testid={`merchant-card-${m.id}`}>
      <Card className="relative bg-[#041A3B] border-white/10 hover:border-[#00E676]/40 transition-[border-color,transform,box-shadow] duration-300 h-full group-hover:-translate-y-0.5 group-hover:shadow-lg group-hover:shadow-[#00E676]/5 overflow-hidden">
        <CardContent className="p-5 sm:p-6 flex flex-col h-full">
          <div className="flex items-start justify-between gap-3 mb-4">
            <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl flex items-center justify-center overflow-hidden flex-shrink-0 border border-white/10"
              style={{ background: `${c}14` }}>
              <Logo m={m} />
            </div>
            {deal ? (
              <div className="text-right" data-testid={`merchant-deal-${m.id}`}>
                <div className="inline-flex items-center gap-1 rounded-lg bg-[#00E676] text-[#000B18] font-outfit font-bold text-sm sm:text-base px-2.5 py-1 shadow-[0_0_18px_rgba(0,230,118,0.35)]">
                  <Tag className="w-3.5 h-3.5" />{deal.label}
                </div>
                <p className="text-[10px] text-[#A0B4CC] mt-1">{deal.sub}</p>
              </div>
            ) : (
              <span className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-full bg-white/5 text-[#A0B4CC] border border-white/10" data-testid={`merchant-perks-${m.id}`}>
                <Sparkles className="w-3 h-3 text-[#00E676]" />Member perks available
              </span>
            )}
          </div>

          <h3 className="font-outfit text-base sm:text-lg font-semibold text-white mb-1 group-hover:text-[#00E676] transition-colors line-clamp-2 break-words">{name}</h3>
          <div className="flex items-center gap-1.5 mb-3 text-[#A0B4CC] text-xs sm:text-sm min-w-0">
            <Building2 className="w-3.5 h-3.5 text-[#5C7599] flex-shrink-0" /><span className="truncate">{industry || 'Business'}</span>
            {eliteExclusive && <span className="ml-auto text-[10px] font-semibold px-2 py-0.5 rounded-full bg-[#FFD740]/15 text-[#FFD740] border border-[#FFD740]/40 flex-shrink-0" data-testid={`merchant-elite-exclusive-${m.id}`}>Elite exclusive</span>}
          </div>

          {m.has_offer && (tier ? (
            m.offer_headline && <p className="text-sm text-white/90 mb-3 line-clamp-2" data-testid={`merchant-offer-headline-${m.id}`}>{m.offer_headline}</p>
          ) : (
            <p className="text-xs text-[#FFD740] mb-3 flex items-center gap-1.5" data-testid={`merchant-offer-locked-${m.id}`}>
              <Lock className="w-3.5 h-3.5" />{loggedIn ? 'Pro & Elite offer · Upgrade to unlock' : 'Members-only offer · Join free, upgrade to unlock'}
            </p>
          ))}
          {!m.has_offer && m.business_description && <p className="text-[#5C7599] text-xs sm:text-sm mb-3 line-clamp-2 break-words">{m.business_description}</p>}

          <div className="mt-auto flex items-center justify-between pt-3 border-t border-white/5 gap-2 min-w-0 text-[11px] sm:text-xs">
            {m.business_address ? (
              <span className="flex items-center gap-1 text-[#5C7599] min-w-0"><MapPin className="w-3 h-3 flex-shrink-0" /><span className="truncate">{m.business_address}</span></span>
            ) : <span />}
            {distance != null ? (
              <span className="inline-flex items-center gap-1 text-[#00E5FF] flex-shrink-0" data-testid={`merchant-distance-${m.id}`}><Navigation className="w-3 h-3" />{distance}</span>
            ) : m.active_services > 0 && (
              <span className="text-[#A0B4CC] flex-shrink-0">{m.active_services} {m.active_services === 1 ? 'deal' : 'deals'}</span>
            )}
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}
